import { useEffect } from "react";

const OVERLAY_SELECTOR = "[data-bottom-obstruction]";
const DOCK_SELECTOR = '[data-bottom-obstruction="dock"]';
const FAMILY_SHORTCUT_SELECTOR = '.family-home-mobile-nav[data-bottom-obstruction="dock"]';
const METRONOME_SELECTOR = ".practice-metronome-launcher, .practice-metronome-mini";
const OPEN_TASK_SELECTOR = '[aria-modal="true"], .sidebar[data-open="true"], .staff-quick-actions--floating [aria-expanded="true"]';
const CSS_PROPERTIES = [
  "--hub-bottom-dock-clearance",
  "--hub-bottom-content-inset",
  "--hub-family-shortcut-clearance",
  "--hub-bottom-metronome-clearance",
] as const;

function isVisible(element: Element, viewportHeight: number): element is HTMLElement {
  const style = window.getComputedStyle(element);
  const opacity = Number.parseFloat(style.opacity);
  if (style.display === "none" || style.visibility === "hidden" || (!Number.isNaN(opacity) && opacity <= 0)) {
    return false;
  }
  const bounds = element.getBoundingClientRect();
  return bounds.width > 0 && bounds.height > 0 && bounds.top < viewportHeight && bounds.bottom > 0;
}

function obstructionFrom(elements: readonly Element[], viewportHeight: number): number {
  let obstruction = 0;
  for (const element of elements) {
    if (!isVisible(element, viewportHeight)) continue;
    obstruction = Math.max(obstruction, viewportHeight - element.getBoundingClientRect().top);
  }
  return Math.max(0, Math.ceil(obstruction));
}

function containsTrackedElement(node: Node): boolean {
  return node instanceof Element
    && (node.matches(`${OVERLAY_SELECTOR},${OPEN_TASK_SELECTOR}`)
      || node.querySelector(`${OVERLAY_SELECTOR},${OPEN_TASK_SELECTOR}`) !== null);
}

/**
 * Shares one measured bottom-obstruction contract between fixed controls,
 * install/update prompts and page scroll padding. Only visible marked elements
 * contribute, so a dismissed prompt or stopped metronome releases its space.
 */
export function BottomOverlayContract() {
  useEffect(() => {
    const root = document.documentElement;
    const original = new Map(CSS_PROPERTIES.map((name) => [
      name,
      { value: root.style.getPropertyValue(name), priority: root.style.getPropertyPriority(name) },
    ]));
    const previousTaskOpen = root.getAttribute("data-overlay-task-open");

    let resizeObserver: ResizeObserver | null = null;
    const measure = () => {
      const elements = Array.from(document.querySelectorAll(OVERLAY_SELECTOR));
      const viewportHeight = window.innerHeight;
      const familyShortcut = obstructionFrom(
        elements.filter((element) => element.matches(FAMILY_SHORTCUT_SELECTOR)),
        viewportHeight,
      );
      root.style.setProperty(CSS_PROPERTIES[2], `${familyShortcut}px`);
      const metronome = obstructionFrom(
        elements.filter((element) => element.matches(METRONOME_SELECTOR)),
        viewportHeight,
      );
      root.style.setProperty(CSS_PROPERTIES[3], `${metronome}px`);

      // Raising the metronome above family shortcuts and staff quick actions
      // above the metronome changes their top edges. Measure all docks only
      // after those CSS variables take effect, then measure prompts again.
      const dock = obstructionFrom(
        elements.filter((element) => element.matches(DOCK_SELECTOR)),
        viewportHeight,
      );
      root.style.setProperty(CSS_PROPERTIES[0], `${dock}px`);
      const content = obstructionFrom(elements, viewportHeight);
      root.style.setProperty(CSS_PROPERTIES[1], `${content}px`);
    };

    const refresh = () => {
      resizeObserver?.disconnect();
      resizeObserver = typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measure);
      for (const element of document.querySelectorAll(OVERLAY_SELECTOR)) {
        resizeObserver?.observe(element);
      }
      if (document.querySelector(OPEN_TASK_SELECTOR)) {
        root.setAttribute("data-overlay-task-open", "true");
      } else {
        root.removeAttribute("data-overlay-task-open");
      }
      measure();
    };

    const mutationObserver = new MutationObserver((records) => {
      const affectsOverlay = records.some((record) => {
        if (record.type === "attributes") {
          if (!(record.target instanceof Element)) return false;
          if (record.attributeName === "aria-modal") return true;
          if (record.attributeName === "data-open" && record.target.matches(".sidebar")) return true;
          if (record.attributeName === "aria-expanded"
            && record.target.closest(".staff-quick-actions--floating") !== null) return true;
          return record.target.matches(`${OVERLAY_SELECTOR},${OPEN_TASK_SELECTOR}`)
            || record.target.closest(`${OVERLAY_SELECTOR},${OPEN_TASK_SELECTOR}`) !== null;
        }
        return [...record.addedNodes, ...record.removedNodes].some(containsTrackedElement);
      });
      if (affectsOverlay) refresh();
    });
    mutationObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ["class", "style", "hidden", "aria-modal", "aria-expanded", "data-open", "data-bottom-obstruction"],
      childList: true,
      subtree: true,
    });

    const onViewportChange = () => measure();
    window.addEventListener("resize", onViewportChange);
    window.visualViewport?.addEventListener("resize", onViewportChange);
    window.visualViewport?.addEventListener("scroll", onViewportChange);
    refresh();

    return () => {
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
      window.removeEventListener("resize", onViewportChange);
      window.visualViewport?.removeEventListener("resize", onViewportChange);
      window.visualViewport?.removeEventListener("scroll", onViewportChange);
      for (const name of CSS_PROPERTIES) {
        const previous = original.get(name)!;
        if (previous.value) root.style.setProperty(name, previous.value, previous.priority);
        else root.style.removeProperty(name);
      }
      if (previousTaskOpen === null) root.removeAttribute("data-overlay-task-open");
      else root.setAttribute("data-overlay-task-open", previousTaskOpen);
    };
  }, []);

  return null;
}
